/**
 * The last line between a button nobody disabled and a write (#3573).
 *
 * Disabling controls one by one will never be complete — 312 files in this app
 * render a `<Button>` — and a control that was missed is a control that still
 * fires. There is no server-side check either: enforcement was considered and
 * deliberately not built (ourteam-backend#4645, closed not-planned). So this
 * interceptor is the outermost guarantee that exists, and it is worth having
 * precisely because it needs nobody to remember anything.
 *
 * It is scoped as tightly as it can be while still being useful:
 *
 *   • only requests to OUR api host — a third party's endpoint is not ours to
 *     block, and blocking one would break a page for no benefit;
 *   • only methods that change something. GET/HEAD/OPTIONS pass, because
 *     reading is exactly what should keep working during a window;
 *   • never auth. A user who cannot sign out is trapped in a session, which is
 *     a worse outcome than the stray write this exists to stop;
 *   • never while a platform admin is signed in — the provider never turns the
 *     flag on for them.
 */
import type { InternalAxiosRequestConfig } from "axios";
import axios from "axios";

const API_BASE: string = import.meta.env.VITE_TALENT_AI_BE_BASE_URL ?? "";

/** Paths that must work even mid-maintenance. Matched on the path, not the
 *  full URL, so a host or a query string cannot smuggle one past. */
const ALWAYS_ALLOWED = [/^\/auth\//i];

const SAFE_METHODS = new Set(["get", "head", "options"]);

/** The event a blocked request fires, so one snackbar can speak for all of
 *  them instead of each call site inventing its own error. */
export const MAINTENANCE_BLOCKED_EVENT = "maintenance:blocked";

/** Set by MaintenanceProvider. Module state because an interceptor runs
 *  outside React and cannot read a context. */
let blocking = false;

export function setMaintenanceBlocking(next: boolean): void {
    blocking = next;
}

export function isMaintenanceBlocking(): boolean {
    return blocking;
}

/** True when this request is a change, aimed at our API, and not on the
 *  allowlist. Exported so a test can reason about it without axios. */
export function shouldBlockRequest(url: string, method: string): boolean {
    if (!blocking) return false;
    if (SAFE_METHODS.has((method || "get").toLowerCase())) return false;
    if (!API_BASE || !url.startsWith(API_BASE)) return false;
    const path = url.slice(API_BASE.length).split("?")[0];
    return !ALWAYS_ALLOWED.some((re) => re.test(path));
}

/** Thrown in place of the request. Carries a marker so a call site that
 *  inspects errors can tell this apart from a network failure. */
export class MaintenancePausedError extends Error {
    readonly isMaintenancePaused = true;
    constructor() {
        super("Changes are paused while scheduled maintenance is in progress.");
        this.name = "MaintenancePausedError";
    }
}

let installed = false;

/** Idempotent: React 18 mounts effects twice in development, and two
 *  interceptors would reject one request with two events. */
export function installMaintenanceGuard(): void {
    if (installed) return;
    installed = true;
    axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        const url = config.url ?? "";
        if (!shouldBlockRequest(url, config.method ?? "get")) return config;
        window.dispatchEvent(new CustomEvent(MAINTENANCE_BLOCKED_EVENT));
        return Promise.reject(new MaintenancePausedError());
    });
}
